import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ── Listagem paginada ──────────────────────────────────────────────────────────
export const useChamadosList = (filters = {}) => {
  const { status = '', tipo = '', prioridade = '', scope = '', page = 1, pageSize = 20 } = filters;
  return useQuery({
    queryKey: ['chamados', { status, tipo, prioridade, scope, page, pageSize }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status)    params.append('status', status);
      if (tipo)      params.append('tipo', tipo);
      if (prioridade) params.append('prioridade', prioridade);
      if (scope)     params.append('scope', scope);
      params.append('page', page);
      params.append('pageSize', pageSize);
      const { data } = await api.get(`/api/chamados?${params.toString()}`);
      // Backend retorna { success, chamados, paginacao: { total, page, pageSize, totalPages } }
      // Normaliza para { data, total, page, pageSize, totalPages }
      return {
        data:       data.chamados      || [],
        total:      data.paginacao?.total     || 0,
        page:       data.paginacao?.page      || 1,
        pageSize:   data.paginacao?.pageSize  || 20,
        totalPages: data.paginacao?.totalPages || 1,
      };
    },
    staleTime: 60 * 1000, // 1 min — lista muda com frequência
    retry: 1,
  });
};

// ── Detalhe com comentários, anexos e histórico ────────────────────────────────
export const useChamadoDetalhe = (id) =>
  useQuery({
    queryKey: ['chamado', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/chamados/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    retry: 1,
  });

// ── Stats (admin) ──────────────────────────────────────────────────────────────
export const useChamadosStats = () =>
  useQuery({
    queryKey: ['chamados-stats'],
    queryFn: async () => {
      const { data } = await api.get('/api/chamados/stats');
      return data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

// ── Criar chamado ──────────────────────────────────────────────────────────────
export const useCriarChamado = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post('/api/chamados', body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamados'] });
      qc.invalidateQueries({ queryKey: ['chamados-stats'] });
    },
  });
};

// ── Comentar ───────────────────────────────────────────────────────────────────
export const useComentar = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post(`/api/chamados/${id}/comentarios`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamado', id] });
    },
  });
};

// ── Upload de anexo ────────────────────────────────────────────────────────────
export const useUploadAnexo = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ arquivo, comentario_id }) => {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      if (comentario_id) fd.append('comentario_id', comentario_id);
      const { data } = await api.post(`/api/chamados/${id}/anexos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamado', id] });
    },
  });
};

// ── Mudar status (admin) ───────────────────────────────────────────────────────
export const useMudarStatus = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ status, comentario }) => {
      const { data } = await api.put(`/api/chamados/${id}/status`, { status, comentario });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamado', id] });
      qc.invalidateQueries({ queryKey: ['chamados'] });
      qc.invalidateQueries({ queryKey: ['chamados-stats'] });
    },
  });
};

// ── Atribuir responsável (admin) ───────────────────────────────────────────────
export const useAtribuirResponsavel = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ responsavel_id }) => {
      const { data } = await api.put(`/api/chamados/${id}/responsavel`, { responsavel_id });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamado', id] });
      qc.invalidateQueries({ queryKey: ['chamados'] });
    },
  });
};

// ── Mudar prioridade (admin) ───────────────────────────────────────────────────
export const useMudarPrioridade = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ prioridade }) => {
      const { data } = await api.put(`/api/chamados/${id}/prioridade`, { prioridade });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chamado', id] });
      qc.invalidateQueries({ queryKey: ['chamados'] });
    },
  });
};
